import apiClient from './client';

import type {
  CanvasAssetType,
  CanvasContextType,
  CanvasLayoutItemsPatchRequest,
  CanvasLayoutNodeRecordRequest,
  CanvasLayoutResponse,
  CanvasLayoutWriteResult,
  CanvasPersistentItemType,
} from '@/types/canvas';

export const CONTEXT_TYPE_API_VALUE: Record<string, number> = {
  asset: 1,
  storyboard: 2,
};

export const ASSET_TYPE_API_VALUE: Record<string, number> = {
  character: 1,
  location: 2,
  prop: 3,
};

export const ITEM_TYPE_API_VALUE: Record<string, number> = {
  resource_pin: 15,
  resourcePin: 15,
  storyboard_temporary_video: 21,
  storyboardTemporaryVideo: 21,
};

const ITEM_TYPE_API_NAME = Object.entries(ITEM_TYPE_API_VALUE).reduce<Record<number, string>>(
  (acc, [name, value]) => {
    if (!name.includes('_')) return acc;
    acc[value] = name;
    return acc;
  },
  {}
);

export function serializeEnumValue(value: unknown, mapping: Record<string, number>): unknown {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return value;

  const mappedValue = mapping[value];
  if (mappedValue !== undefined) return mappedValue;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : value;
}

function deserializeCanvasPersistentItemType(value: unknown): CanvasPersistentItemType {
  if (typeof value === 'number') {
    const mappedValue = ITEM_TYPE_API_NAME[value];
    if (mappedValue) return mappedValue as CanvasPersistentItemType;
  }
  return value as CanvasPersistentItemType;
}

function readObjectValue(record: Record<string, unknown>, camelKey: string, snakeKey: string) {
  return record[camelKey] ?? record[snakeKey];
}

function readNumberOrDefault(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeCanvasLayoutItem(
  item: CanvasLayoutResponse['items'][number],
  index: number
): CanvasLayoutResponse['items'][number] {
  const raw = item as unknown as Record<string, unknown>;
  const metaData = readObjectValue(raw, 'metaData', 'meta_data');

  return {
    ...item,
    canvasItemId: readObjectValue(raw, 'canvasItemId', 'canvas_item_id') as string,
    itemType: deserializeCanvasPersistentItemType(readObjectValue(raw, 'itemType', 'item_type')),
    itemId: readObjectValue(raw, 'itemId', 'item_id') as string,
    sourceResourceType: readObjectValue(
      raw,
      'sourceResourceType',
      'source_resource_type'
    ) as CanvasLayoutResponse['items'][number]['sourceResourceType'],
    sourceResourceId: readObjectValue(raw, 'sourceResourceId', 'source_resource_id') as
      | string
      | null
      | undefined,
    x: readNumberOrDefault(readObjectValue(raw, 'x', 'x'), 0),
    y: readNumberOrDefault(readObjectValue(raw, 'y', 'y'), 0),
    zIndex: readNumberOrDefault(readObjectValue(raw, 'zIndex', 'z_index'), index),
    metaData:
      metaData && typeof metaData === 'object' && !Array.isArray(metaData)
        ? (metaData as Record<string, unknown>)
        : {},
    createdAt: readObjectValue(raw, 'createdAt', 'created_at') as string,
    updatedAt: readObjectValue(raw, 'updatedAt', 'updated_at') as string,
  };
}

function isPersistentApiItemType(itemType: string): boolean {
  return ITEM_TYPE_API_VALUE[itemType] !== undefined;
}

export function serializeCanvasLayoutNodeRecordRequest(
  payload: CanvasLayoutNodeRecordRequest | CanvasLayoutItemsPatchRequest
) {
  return {
    ...payload,
    contextType: serializeEnumValue(payload.contextType, CONTEXT_TYPE_API_VALUE),
    assetType:
      payload.assetType == null
        ? payload.assetType
        : serializeEnumValue(payload.assetType, ASSET_TYPE_API_VALUE),
    items: payload.items
      .filter((item) => isPersistentApiItemType(item.itemType))
      .map((item) => ({
        itemType: serializeEnumValue(item.itemType, ITEM_TYPE_API_VALUE),
        itemId: item.itemId,
        ...('sourceResourceType' in item ? { sourceResourceType: item.sourceResourceType } : {}),
        ...('sourceResourceId' in item ? { sourceResourceId: item.sourceResourceId } : {}),
        ...(item.metaData ? { metaData: item.metaData } : {}),
      })),
  };
}

export const serializeCanvasLayoutItemsPatchRequest = serializeCanvasLayoutNodeRecordRequest;

function normalizeCanvasLayoutResponse(response: CanvasLayoutResponse): CanvasLayoutResponse {
  return {
    ...response,
    items: response.items.map(normalizeCanvasLayoutItem),
  };
}

export const canvasLayoutApi = {
  async getLayout(params: {
    projectId: string;
    contextType: CanvasContextType;
    assetType?: CanvasAssetType | null;
    assetId?: string | null;
    storyboardId?: string | null;
  }): Promise<CanvasLayoutResponse> {
    const response = (await apiClient.get(`/projects/${params.projectId}/canvas/items`, {
      params: {
        contextType: params.contextType,
        assetType: params.assetType ?? undefined,
        assetId: params.assetId ?? undefined,
        storyboardId: params.storyboardId ?? undefined,
      },
    })) as unknown as CanvasLayoutResponse;
    return normalizeCanvasLayoutResponse(response);
  },

  patchLayoutItems(
    projectId: string,
    payload: CanvasLayoutItemsPatchRequest
  ): Promise<CanvasLayoutWriteResult> {
    return apiClient.patch(
      `/projects/${projectId}/canvas/items`,
      serializeCanvasLayoutItemsPatchRequest(payload)
    );
  },

  patchNodeRecords(
    projectId: string,
    payload: CanvasLayoutNodeRecordRequest
  ): Promise<CanvasLayoutWriteResult> {
    return apiClient.patch(
      `/projects/${projectId}/canvas/items`,
      serializeCanvasLayoutNodeRecordRequest(payload)
    );
  },
};
